/**
 * Busca de paciente com autocomplete debounced (≥2 caracteres).
 *
 * Componente reusável extraído do wizard de Pedidos de Lente (Sprint 3) para
 * poder ser aplicado em qualquer tela que precise selecionar 1 paciente — em
 * vez de cada tela baixar a coleção inteira num `<select>`.
 *
 * Comportamento:
 *   - Dispara a busca apenas após 2 caracteres digitados
 *   - Debounce de 250 ms (não consulta o banco a cada tecla)
 *   - Auto-detecta CPF vs nome (heurística idêntica à `GlobalPatientSearch`)
 *   - Usa `PatientService.searchPatients`, que bate nos índices
 *     `nameNormalized` / `cpfPrefix` / `cpfHash`
 *   - Quando um paciente é selecionado, mostra um "chip" compacto com botão X
 *
 * O valor é sempre o `Patient` completo (não só id+nome), porque consumidores
 * diferentes precisam de campos diferentes — o wizard LC só usa `id` e `name`,
 * mas o formulário TISS precisa de `insurers[]` para preencher dados do
 * convênio automaticamente.
 */

import { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { PatientService } from '@/services/firebase/patients';
import { PatientMetaLines } from '@/components/patients/PatientMetaLines';
import type { Patient } from '@/types/models';

export interface PatientTypeaheadProps {
  clinicId: string;
  value: Patient | null;
  onChange: (patient: Patient | null) => void;
  label?: string;
  placeholder?: string;
}

export function PatientTypeahead({
  clinicId,
  value,
  onChange,
  label = 'Paciente',
  placeholder = 'Digite nome ou CPF (mín. 2 caracteres)…',
}: PatientTypeaheadProps) {
  const [searchText, setSearchText] = useState('');
  const [results, setResults] = useState<Patient[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Fecha o dropdown ao clicar fora.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Busca debounced (250 ms) a partir de 2+ caracteres.
  useEffect(() => {
    if (!clinicId) {
      setResults(null);
      return;
    }
    const trimmed = searchText.trim();
    if (trimmed.length < 2) {
      setResults(null);
      setError(null);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const digitsOnly = trimmed.replace(/\D/g, '');
        const looksLikeCpf = digitsOnly.length >= 2 && /^[\d.\-/]+$/.test(trimmed);
        const mode = looksLikeCpf ? 'cpf' : 'name';
        const res = await PatientService.searchPatients(clinicId, trimmed, mode);
        setResults(res);
        setOpen(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Falha na busca.');
        setResults([]);
        setOpen(true);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [searchText, clinicId]);

  // Estado "paciente selecionado": chip compacto com opção de trocar.
  if (value) {
    return (
      <div>
        <label className="block text-xs text-on-surface-variant mb-1">
          {label}
        </label>
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-container/40 border border-primary/20">
          <div className="w-7 h-7 rounded-full bg-primary text-on-primary flex items-center justify-center text-xs font-bold shrink-0">
            {value.name.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 text-sm text-on-surface truncate">
            {value.name}
          </div>
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setSearchText('');
              setResults(null);
              setOpen(false);
            }}
            className="text-on-surface-variant hover:text-on-surface transition-colors"
            aria-label="Trocar paciente"
            title="Trocar paciente"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    );
  }

  // Estado "buscando": input + dropdown.
  return (
    <div ref={wrapperRef} className="relative">
      <label className="block text-xs text-on-surface-variant mb-1">
        {label}
      </label>
      <div className="relative">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant pointer-events-none"
        />
        <input
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onFocus={() => {
            if (results) setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
          }}
          placeholder={placeholder}
          className="w-full pl-9 pr-4 py-2 rounded-lg text-sm bg-surface-container text-on-surface placeholder-on-surface-variant outline-none focus:ring-2 focus:ring-primary/40 border border-outline-variant/40"
        />
      </div>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-surface border border-outline-variant/20 rounded-xl shadow-lg max-h-72 overflow-y-auto">
          {loading && (
            <div className="px-4 py-3 text-sm text-on-surface-variant animate-pulse">
              Buscando…
            </div>
          )}
          {!loading && error && (
            <div className="px-4 py-3 text-sm text-error">{error}</div>
          )}
          {!loading && !error && results && results.length === 0 && (
            <div className="px-4 py-3 text-sm text-on-surface-variant text-center">
              Nenhum paciente encontrado.
            </div>
          )}
          {!loading && !error && results && results.length > 0 && (
            <ul>
              {results.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(p);
                      setSearchText('');
                      setResults(null);
                      setOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-primary/10 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary-container text-primary flex items-center justify-center text-xs font-bold shrink-0">
                      {p.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-on-surface truncate">
                        {p.name}
                      </div>
                      <PatientMetaLines
                        p={p}
                        incomplete={p.registrationStatus === 'MINIMAL'}
                      />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
